import React, { useState } from 'react'

import { NavLink as RouterNavLink, NavLinkProps, useParams } from 'react-router-dom'
import { Add as AddIcon } from '@material-ui/icons'

import { useQueryPaginator } from 'src/components/Table'
import { AppointmentDetailTable } from 'src/components/Table/detail/Appointment'
import { AppointmentDialog } from 'src/modules/supply_chain/components/AppointmentDialog'
import { EventListTable } from 'src/components/Table/list/Event'
import { AddAttachmentsDialog } from 'src/components/Dialog/AddAttachmentsDialog'
import { GenericPreview, PaginatedFilePreviewGallery } from 'src/components/FilePreview'
import { Workflow } from 'src/modules/supply_chain/components/Workflow'

import { Appointment, Tag } from 'src/api/models'
import { skuit } from 'src/services/skuit'
import { useQuery, UseQueryResult } from 'react-query'

import { DetailPage } from 'src/components/Pages/DetailPage'
import { DetailTab } from 'src/components/Pages/Tab'

import { Chip } from '@material-ui/core'

const NavLink = React.forwardRef((props: NavLinkProps, ref: React.Ref<HTMLAnchorElement>) => (
  <RouterNavLink innerRef={ref} {...props} />
))
NavLink.displayName = 'NavLink'

interface PathProps {
  guid: string
}

interface WorkflowTabProps {
  appointment: UseQueryResult<Appointment>
}

const WorkflowTab = (props: WorkflowTabProps) => {
  const { appointment } = props

  return (
    <DetailTab>
      <Workflow
        title="Appointment Workflow"
        loading={appointment.isFetching}
        workflow={appointment.data?.workflow || []}
        status={appointment.data?.status || ''}
        actions={appointment.data?.getWorkflowActions(() => appointment.refetch()) || {}}
      />
    </DetailTab>
  )
}

const AttachmentTab = () => {
  const { guid } = useParams<PathProps>()
  const [addAttachmentDialogOpen, setAddAttachmentDialogOpen] = useState(false)
  const attachmentsPaginator = useQueryPaginator(
    'attachment-list',
    () => skuit.sc.appointments.attachments.list(guid),
    {
      params: { page_size: 5, references: guid },
    }
  )

  return (
    <>
      {addAttachmentDialogOpen && (
        <AddAttachmentsDialog
          onSuccess={() => attachmentsPaginator.refresh()}
          addAttachment={(config) =>
            skuit.sc.appointments.attachments.create(guid, config?.data, config)
          }
          open={addAttachmentDialogOpen}
          handleClose={() => {
            setAddAttachmentDialogOpen(false)
          }}
        />
      )}
      <DetailTab>
        <PaginatedFilePreviewGallery
          title="Attachments"
          paginator={attachmentsPaginator}
          getIdent={(item) => item.guid}
          onAdd={() => {
            setAddAttachmentDialogOpen(true)
          }}
          renderItem={(item) => {
            return (
              <>
                <GenericPreview url={item.file} />
                <div>
                  {item.tags.map((tag: Tag) => (
                    <Chip key={tag.slug} label={tag.name} />
                  ))}
                </div>
              </>
            )
          }}
        />
      </DetailTab>
    </>
  )
}

const EventsTab = () => {
  const { guid } = useParams<PathProps>()

  const eventsPaginator = useQueryPaginator('event-list', skuit.sc.events.list, {
    params: { page_size: 5, references: guid },
  })

  return (
    <DetailTab>
      <EventListTable
        paginator={eventsPaginator}
        title="Appointment Events"
        fields={['type', 'data', 'by', 'on']}
      />
    </DetailTab>
  )
}

export const AppointmentPage = () => {
  const { guid } = useParams<PathProps>()
  const [createOpen, setCreateOpen] = useState(false)

  const appointment = useQuery(['appointment-details', guid], () =>
    skuit.sc.appointments.retrieve(guid)
  )
  const eventsPaginator = useQueryPaginator('event-list', skuit.sc.events.list, {
    params: { page_size: 5, references: guid },
  })

  const refresh = () => {
    appointment.refetch()
    eventsPaginator.refresh()
  }

  return (
    <>
      {createOpen && (
        <AppointmentDialog
          open={createOpen}
          handleClose={() => {
            setCreateOpen(false)
          }}
        />
      )}
      <DetailPage
        obj={appointment}
        title="Appointment"
        refresh={refresh}
        breadcrumbs={[
          { title: 'Dashboard', url: '/' },
          { title: 'Appointments', url: '/shipping/appointments' },
        ]}
        detailComponent={<AppointmentDetailTable obj={appointment} refresh={refresh} />}
        menuItems={[
          {
            Icon: AddIcon,
            actions: [{ title: 'New Appointment', onClick: () => setCreateOpen(true) }],
          },
        ]}
        tabs={['Workflow', 'Attachments', 'Events']}
      >
        <WorkflowTab appointment={appointment} />
        <AttachmentTab />
        <EventsTab />
      </DetailPage>
    </>
  )
}
